import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { IconActionDropDown, LoadingSpinner } from 'wg-fe-ui';
import { Model } from '../../../../types/SMT';
import { customScrollBar } from '../utils/styledComponentsUtils';
import { VersionsProps } from './VehicleLookupOrderTable';

interface Props {
  versions: VersionsProps[];
  fuel: string;
  model: Model;
  selected?: string;
  searchLoading: boolean;
  noResults: boolean;
  dataTestId: string;
  isAppending: boolean;
  handleSelectedCar: (b: string, m: string, v: string) => void;
}
const VehicleLookupOrderTableList = React.forwardRef<HTMLUListElement, Props>(
  (
    {
      versions,
      fuel,
      model,
      selected,
      searchLoading,
      noResults,
      handleSelectedCar,
      dataTestId,
      isAppending,
    },
    ref,
  ) => {
    const { t } = useTranslation();

    return (
      <VersionList
        ref={ref}
        disabled={versions?.length === 0 || noResults}
        key={`${fuel} - ${model?.id}`}
      >
        {searchLoading ? (
          <CenteredLoadingWrapper>
            <LoadingSpinner />
          </CenteredLoadingWrapper>
        ) : versions?.length === 0 || noResults ? (
          <NoResults>{t('No results')}</NoResults>
        ) : (
          <>
            {versions?.map((version) => (
              <VersionItem
                key={version.id}
                isSelected={selected === version.id}
                onClick={() =>
                  handleSelectedCar(version.brand, model.id, version.id)
                }
                data-test-id={`${dataTestId}_version_option`}
              >
                <Version>
                  {`${version.brand} ${version.model} ${version.version}`}
                </Version>
                <Kw>{version.kw}</Kw>
                <CC>{version.cc}</CC>
                <CoTwo>{version.co2}</CoTwo>
                <ModelYear>{version.modelYear}</ModelYear>
                <Price>
                  {version.price
                    ? Number(version.price).toLocaleString('nl-BE', {
                        style: 'currency',
                        currency: 'EUR',
                      })
                    : t(`Unknown`)}
                  <HiddenIcon />
                </Price>
              </VersionItem>
            ))}
            {isAppending && (
              <AppendingLoadingWrapper>
                <LoadingSpinner />
              </AppendingLoadingWrapper>
            )}
          </>
        )}
      </VersionList>
    );
  },
);

const VersionList = styled.ul<{ disabled: boolean }>`
  background-color: ${({ disabled }) => (disabled ? 'hsl(0,0%,95%)' : 'white')};
  border-radius: 0 0 3px 3px;
  height: 30rem;
  overflow-y: scroll;
  width: 100%;

  ${customScrollBar}
`;
const CenteredLoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  height: 100%;
`;

const VersionItem = styled.li<{ isSelected: boolean }>`
  display: flex;
  justify-content: space-between;
  font-size: 1.5rem;
  line-height: 1.6;
  text-align: left;
  color: ${({ theme, isSelected }) => (isSelected ? 'white' : theme.typo.text)};
  width: 100%;

  background-color: ${({ theme, isSelected }) =>
    isSelected ? theme.brand.primary : 'none'};
  cursor: pointer;
  padding: 1rem 0 1rem 3rem;

  & > span:last-child {
    text-align: right;
  }

  :hover {
    opacity: 0.8;
    background-color: ${({ theme }) => theme.brand.secondary};
    color: white;
  }
`;

const NoResults = styled.div`
  font-size: 2.1rem;
  display: flex;
  justify-content: center;
  height: 100%;
  align-items: center;
`;

const VersionLabelItem = styled.div`
  display: inline-flex;
  align-items: center;
`;

const Version = styled(VersionLabelItem)`
  width: 40%;
  padding-right: 2rem;
`;

const Kw = styled(VersionLabelItem)`
  width: 11%;
  padding-right: 1rem;
`;

const ModelYear = styled(VersionLabelItem)`
  width: 11%;
  padding-right: 1rem;
`;

const CC = styled(VersionLabelItem)`
  width: 11%;
  padding-right: 1rem;
`;

const CoTwo = styled(VersionLabelItem)`
  width: 11%;
  padding-right: 1rem;
`;

const Price = styled(VersionLabelItem)`
  justify-content: flex-end;
  text-align: right;
  width: 16%;
`;

const AppendingLoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 4rem 2rem;
`;

const StyledIconActionDropDown = styled(IconActionDropDown)`
  height: 2rem;
  width: 25;
`;

const HiddenIcon = styled(StyledIconActionDropDown)`
  visibility: hidden;
`;

VehicleLookupOrderTableList.displayName = 'VehicleLookupOrderTableList';
export default VehicleLookupOrderTableList;
